import { employeeRangeSchema, type EmployeeRangeValue } from '@houzy/schemas/partner-cockpit/company';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useEffect, useState, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { uidDigits } from '../../../components/companyUid';
import { CompanyUidField } from '../../../components/CompanyUidField';
import { apiErrorMessageKey } from '../../../lib/api';
import { uploadCompanyLogo, useSaveCompanyProfile } from '../api/profileApi';
import { EMPLOYEE_LABEL_KEYS } from '../labels';
import { useDirtyForm } from '../state/useDirtyForm';
import { type CompanyProfile } from '../types';
import { LogoUpload } from './LogoUpload';
import { SettingsCard } from './SettingsCard';

export type CompanyProfileCardProps = {
    profile: CompanyProfile;
    readOnly?: boolean;
    // The onboarding application has nowhere to keep a description and no partner to own a logo, and approval carries
    // neither, so before validation the two fields are left out rather than collected and dropped.
    withoutLogoAndDescription?: boolean;
    // Zefix register's name, which approval copies onto the partner.
    withNameFromRegister?: boolean;
};

const DESCRIPTION_MAX_CHARS = 999;

const EMPLOYEE_RANGE_SET: ReadonlySet<string> = new Set(employeeRangeSchema.options);

function isEmployeeRange(value: string): value is EmployeeRangeValue {
    return EMPLOYEE_RANGE_SET.has(value);
}

export function CompanyProfileCard({
    profile,
    readOnly = false,
    withoutLogoAndDescription = false,
    withNameFromRegister = false,
}: CompanyProfileCardProps): ReactElement {
    const { t } = useTranslation();
    const { value, setValue, reset, isDirty } = useDirtyForm<CompanyProfile>(profile);
    const saveMutation = useSaveCompanyProfile();

    const [pendingLogoFile, setPendingLogoFile] = useState<File | undefined>(undefined);
    const [pendingLogoUrl, setPendingLogoUrl] = useState<string | undefined>(undefined);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    useEffect((): (() => void) => {
        return () => {
            if (pendingLogoUrl) {
                URL.revokeObjectURL(pendingLogoUrl);
            }
        };
    }, [pendingLogoUrl]);

    const update = <K extends keyof CompanyProfile>(key: K, next: CompanyProfile[K]): void => {
        setValue((prev) => ({ ...prev, [key]: next }));
    };

    const handleLogoChange = (file: File | undefined): void => {
        if (pendingLogoUrl) {
            URL.revokeObjectURL(pendingLogoUrl);
        }
        const nextUrl = file ? URL.createObjectURL(file) : undefined;
        setPendingLogoFile(file);
        setPendingLogoUrl(nextUrl);
        update('logoUrl', nextUrl);
    };

    const handleEmployees = (event: ChangeEvent<HTMLInputElement>): void => {
        const raw = event.target.value;
        update('employees', isEmployeeRange(raw) ? raw : undefined);
    };

    const handleSave = async (): Promise<void> => {
        let logoUrl = value.logoUrl;
        if (pendingLogoFile) {
            setIsUploadingLogo(true);
            try {
                logoUrl = await uploadCompanyLogo(pendingLogoFile);
                setPendingLogoFile(undefined);
            } finally {
                setIsUploadingLogo(false);
            }
        }
        saveMutation.mutate({ ...value, logoUrl });
    };

    const isValidUrl = (url: string): boolean => {
        if (!url) {
            return true;
        }
        const normalized = url.startsWith('http') ? url : `https://${url}`;
        return URL.canParse(normalized);
    };

    const hasNameError = isDirty && !value.companyName.trim();
    const hasWebsiteError = isDirty && !isValidUrl(value.website);
    const hasPostcodeError = isDirty && !!value.postcode && !/^\d{4}$/.test(value.postcode.trim());
    // Not gated on `isDirty` like the fields above: those always arrive populated, whereas a partner from before
    // the street/nr split has no number yet. Tying it to the form being dirty would surface the error only once
    // some unrelated field was edited, which reads as that edit having caused it.
    const hasStreetError = !value.street.trim();
    const hasNrError = !value.nr.trim();
    const hasErrors = hasNameError || hasWebsiteError || hasPostcodeError || hasStreetError || hasNrError;

    const descriptionRemaining = DESCRIPTION_MAX_CHARS - value.description.length;

    const descriptionHelper =
        descriptionRemaining < 100
            ? t('SETTINGS.COMPANY.PROFILE.CHARACTERS_REMAINING', { count: descriptionRemaining })
            : undefined;

    return (
        <SettingsCard
            title={t('SETTINGS.COMPANY.PROFILE.TITLE')}
            dirty={isDirty}
            saving={saveMutation.isPending || isUploadingLogo}
            onSave={handleSave}
            onReset={reset}
            disableSave={hasErrors}
            readOnly={readOnly}>
            <Stack spacing={2.5}>
                {saveMutation.isError && (
                    <Alert severity="error">
                        {t(apiErrorMessageKey(saveMutation.error, 'SETTINGS.COMPANY.PROFILE.ERRORS.SAVE_FAILED'))}
                    </Alert>
                )}
                {withoutLogoAndDescription ? undefined : (
                    <LogoUpload logoUrl={value.logoUrl} onChange={handleLogoChange} />
                )}

                <TextField
                    label={t('SETTINGS.COMPANY.PROFILE.NAME')}
                    fullWidth
                    autoComplete="organization"
                    value={value.companyName}
                    onChange={(e) => update('companyName', e.target.value)}
                    error={hasNameError}
                    helperText={hasNameError ? t('SETTINGS.COMPANY.PROFILE.NAME_ERROR') : undefined}
                    disabled={readOnly || withNameFromRegister}
                />

                {withoutLogoAndDescription ? undefined : (
                    <TextField
                        label={t('SETTINGS.COMPANY.PROFILE.DESCRIPTION')}
                        placeholder={t('SETTINGS.COMPANY.PROFILE.DESCRIPTION_PLACEHOLDER')}
                        fullWidth
                        multiline
                        minRows={4}
                        value={value.description}
                        onChange={(e) => update('description', e.target.value.slice(0, DESCRIPTION_MAX_CHARS))}
                        helperText={descriptionHelper}
                        disabled={readOnly}
                    />
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Stack sx={{ flex: { md: 1 } }}>
                        {/* Shown, never edited: the UID is entered and verified on the validation. */}
                        <CompanyUidField
                            label={t('SETTINGS.COMPANY.PROFILE.COMPANY_ID')}
                            value={uidDigits(value.companyUid)}
                            disabled
                        />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: { md: 1 } }}>
                        <TextField
                            label={t('SETTINGS.COMPANY.PROFILE.ADDRESS')}
                            fullWidth
                            sx={{ flex: { sm: 4 } }}
                            autoComplete="address-line1"
                            value={value.street}
                            onChange={(e) => update('street', e.target.value)}
                            error={hasStreetError}
                            helperText={hasStreetError ? t('SETTINGS.COMPANY.PROFILE.ADDRESS_ERROR') : undefined}
                            disabled={readOnly}
                        />
                        <TextField
                            label={t('SETTINGS.COMPANY.PROFILE.ADDRESS_NR')}
                            fullWidth
                            sx={{ flex: { md: 1 } }}
                            value={value.nr}
                            onChange={(e) => update('nr', e.target.value)}
                            error={hasNrError}
                            helperText={hasNrError ? t('SETTINGS.COMPANY.PROFILE.ADDRESS_NR_ERROR') : undefined}
                            disabled={readOnly}
                        />
                    </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                        label={t('SETTINGS.COMPANY.PROFILE.CITY')}
                        fullWidth
                        autoComplete="address-level2"
                        value={value.city}
                        onChange={(e) => update('city', e.target.value)}
                        disabled={readOnly}
                    />
                    <TextField
                        label={t('SETTINGS.COMPANY.PROFILE.POSTCODE')}
                        fullWidth
                        autoComplete="postal-code"
                        value={value.postcode}
                        onChange={(e) => update('postcode', e.target.value)}
                        error={hasPostcodeError}
                        helperText={hasPostcodeError ? t('SETTINGS.COMPANY.PROFILE.POSTCODE_ERROR') : undefined}
                        disabled={readOnly}
                    />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                        label={t('SETTINGS.COMPANY.PROFILE.WEBSITE')}
                        fullWidth
                        autoComplete="url"
                        value={value.website}
                        onChange={(e) => update('website', e.target.value)}
                        error={hasWebsiteError}
                        helperText={hasWebsiteError ? t('SETTINGS.COMPANY.PROFILE.WEBSITE_ERROR') : undefined}
                        disabled={readOnly}
                    />
                    <TextField
                        select
                        label={t('SETTINGS.COMPANY.PROFILE.EMPLOYEES')}
                        fullWidth
                        value={value.employees ?? ''}
                        onChange={handleEmployees}
                        disabled={readOnly}>
                        {employeeRangeSchema.options.map((bucket) => (
                            <MenuItem key={bucket} value={bucket}>
                                {t(EMPLOYEE_LABEL_KEYS[bucket])}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Stack>
        </SettingsCard>
    );
}
